/**
 * DataLayer utility functions for Google Analytics tracking
 */

/**
 * Push event to Google Analytics Data Layer
 * @param {string} event - Event name
 * @param {Object} parameters - Event parameters
 */
export const pushDataLayerEvent = (event, parameters = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event,
      ...parameters,
      timestamp: new Date().toISOString()
    })
  }
}

/**
 * Track account creation event
 * @param {Object} userData - User data object
 * @param {string} userData.userId - User ID
 * @param {string} userData.email - User email
 * @param {string} userData.firstName - User first name
 * @param {string} userData.lastName - User last name
 * @param {string} userData.objective - User objective/purpose
 * @param {string} userData.source - Registration source
 */
export const trackAccountCreation = (userData) => {
  pushDataLayerEvent('account_creation', {
    userId: userData.userId || '',
    email: userData.email || '',
    firstName: userData.firstName || '',
    lastName: userData.lastName || '',
    objective: userData.objective || '',
    source: userData.source || 'web',
    eventCategory: 'User Registration',
    eventAction: 'Account Created'
  })
}

/**
 * Track patient login event
 * @param {Object} userData - User data object
 * @param {string} userData.userId - User ID
 * @param {string} userData.email - User email
 * @param {number} userData.loginCount - Number of logins
 * @param {string} userData.objective - User objective/purpose
 * @param {string} userData.source - Login source
 */
export const trackPatientLogin = (userData) => {
  pushDataLayerEvent('patient_login', {
    userId: userData.userId || '',
    email: userData.email || '',
    loginCount: userData.loginCount || 0,
    objective: userData.objective || '',
    source: userData.source || 'web',
    eventCategory: 'User Authentication',
    eventAction: 'Login Completed'
  })
}

/**
 * Track first-time login event (new user)
 * @param {Object} userData - User data object
 * @param {string} userData.userId - User ID
 * @param {string} userData.email - User email
 * @param {string} userData.objective - User objective/purpose
 * @param {string} userData.source - Registration source
 */
export const trackFirstTimeLogin = (userData) => {
  pushDataLayerEvent('first_time_login', {
    userId: userData.userId || '',
    email: userData.email || '',
    objective: userData.objective || '',
    source: userData.source || 'web',
    eventCategory: 'User Registration',
    eventAction: 'First Login'
  })
}

/**
 * Track onboarding completion event
 * @param {Object} userData - User data object
 * @param {string} userData.userId - User ID
 * @param {string} userData.objective - User objective/purpose
 * @param {string} userData.onboardingType - Type of onboarding completed
 */
export const trackOnboardingCompletion = (userData) => {
  pushDataLayerEvent('onboarding_completion', {
    userId: userData.userId || '',
    objective: userData.objective || '',
    onboardingType: userData.onboardingType || 'standard',
    eventCategory: 'User Onboarding',
    eventAction: 'Onboarding Completed'
  })
}

/**
 * Track appointment booking success event
 *
 * NOTA: Esta función es idempotente por combinación (userId, appointmentId)
 * dentro de la misma sesión para evitar duplicados cuando el flujo
 * dispara el tracking desde más de un lugar (por ejemplo, PaymentMethod + ConfirmationView).
 *
 * @param {Object} appointmentData - Appointment data object
 * @param {string} appointmentData.userId - User ID
 * @param {string} appointmentData.appointmentId - Appointment ID (or IDs for packages)
 * @param {string} appointmentData.serviceName - Service name
 * @param {string} appointmentData.serviceType - Service type/specialty
 * @param {string} appointmentData.appointmentType - Type of appointment (single/package)
 * @param {string} appointmentData.modality - Appointment modality (Virtual/Presencial)
 * @param {string} appointmentData.doctorName - Doctor name
 * @param {string} appointmentData.clinicName - Clinic name
 * @param {boolean} appointmentData.isPaid - Whether appointment was paid
 * @param {number} appointmentData.totalAmount - Total amount paid
 * @param {string} appointmentData.paymentMethod - Payment method used
 * @param {string} appointmentData.transactionId - Payment transaction identifier (for online payments)
 * @param {string} appointmentData.currency - Payment currency (e.g., MXN)
 * @param {string} appointmentData.entrypoint - Entry point source
 * @param {string} appointmentData.source - Booking source
 */
export const trackAppointmentBooked = (appointmentData) => {
  // Evitar duplicados en la misma sesión para la misma cita
  if (typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined') {
    const userId = appointmentData.userId || ''
    const appointmentId = appointmentData.appointmentId || ''

    if (appointmentId) {
      const dedupeKey = `${userId}_${appointmentId}`
      const lastKey = window.sessionStorage.getItem('last_appointment_booked_key')

      if (lastKey === dedupeKey) {
        // Ya se envió este evento para esta combinación userId/appointmentId
        return
      }

      window.sessionStorage.setItem('last_appointment_booked_key', dedupeKey)
    }
  }

  pushDataLayerEvent('appointment_booked', {
    userId: appointmentData.userId || '',
    appointmentId: appointmentData.appointmentId || '',
    serviceName: appointmentData.serviceName || '',
    serviceType: appointmentData.serviceType || '',
    appointmentType: appointmentData.appointmentType || '',
    modality: appointmentData.modality || '',
    doctorName: appointmentData.doctorName || '',
    clinicName: appointmentData.clinicName || '',
    isPaid: appointmentData.isPaid || false,
    totalAmount: appointmentData.totalAmount || 0,
    paymentMethod: appointmentData.paymentMethod || '',
    transactionId: appointmentData.transactionId || '',
    currency: appointmentData.currency || 'MXN',
    entrypoint: appointmentData.entrypoint || '',
    source: appointmentData.source || 'web',
    eventCategory: 'Appointment Booking',
    eventAction: 'Appointment Successfully Booked'
  })
}
